import { supabase } from '../lib/supabase';

/**
 * SMS Notification Service
 * Handles phone number subscriptions for SMS-based feature notifications
 */
export const smsNotificationService = {
  /**
   * Subscribe a phone number to SMS notifications
   * @param {string} phoneNumber - Phone number to subscribe
   * @param {string} countryCode - Country code (default: +1)
   * @returns {Promise<Object>} Subscription result
   */
  async subscribe(phoneNumber, countryCode = '+1') {
    try {
      // Format phone number (remove spaces, dashes, etc.)
      const formattedPhone = phoneNumber?.replace(/[\s\-\(\)]/g, '');
      
      if (!formattedPhone || formattedPhone?.length < 10) {
        throw new Error('Please enter a valid phone number');
      }

      const { data, error } = await supabase?.from('sms_notifications')?.insert({
          phone_number: formattedPhone,
          country_code: countryCode,
          source: 'footer_signup',
          status: 'active'
        })?.select()?.single();

      if (error) {
        // Handle duplicate phone number
        if (error?.code === '23505') {
          throw new Error('This phone number is already subscribed');
        }
        throw error;
      }

      return {
        success: true,
        data: {
          id: data?.id,
          phoneNumber: data?.phone_number,
          countryCode: data?.country_code,
          subscribedAt: data?.subscribed_at
        }
      };
    } catch (error) {
      console.error('SMS subscription error:', error);
      throw error;
    }
  },

  /**
   * Check if a phone number is already subscribed
   * @param {string} phoneNumber - Phone number to check
   * @returns {Promise<boolean>} True if subscribed
   */
  async isSubscribed(phoneNumber) {
    try {
      const formattedPhone = phoneNumber?.replace(/[\s\-\(\)]/g, '');
      
      const { data, error } = await supabase?.from('sms_notifications')?.select('id')?.eq('phone_number', formattedPhone)?.eq('status', 'active')?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      return !!data;
    } catch (error) {
      console.error('Check subscription error:', error);
      return false;
    }
  },

  /**
   * Get total count of SMS subscribers
   * @returns {Promise<number>} Total subscriber count
   */
  async getSubscriberCount() {
    try {
      const { count, error } = await supabase?.from('sms_notifications')?.select('*', { count: 'exact', head: true })?.eq('status', 'active');

      if (error) throw error;

      return count || 0;
    } catch (error) {
      console.error('Get subscriber count error:', error);
      return 0;
    }
  }
};